import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from './contexts/AuthContext'
import { ProfileProvider } from './contexts/ProfileContext'
import { ToastProvider } from './contexts/ToastContext'
import { UrnOverrideProvider } from './contexts/UrnOverrideContext'
import { ErrorBoundary } from './components/ErrorBoundary'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { WorkspaceInitializer } from './components/auth/WorkspaceInitializer'
import { Spinner } from './components/Spinner'

// Configure React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
})

// Lazy load screens for code splitting
const AuthScreen = lazy(() =>
  import('./screens/AuthScreen').then((module) => ({ default: module.AuthScreen }))
)
const DashboardScreen = lazy(() =>
  import('./screens/DashboardScreen').then((module) => ({
    default: module.DashboardScreen,
  }))
)
const ConnectionsScreen = lazy(() =>
  import('./screens/ConnectionsScreen').then((module) => ({
    default: module.ConnectionsScreen,
  }))
)
const ComparisonsScreen = lazy(() =>
  import('./screens/ComparisonsScreen').then((module) => ({
    default: module.ComparisonsScreen,
  }))
)
const CompareSetupScreen = lazy(() =>
  import('./screens/CompareSetupScreen').then((module) => ({
    default: module.CompareSetupScreen,
  }))
)
const DiffViewerScreen = lazy(() =>
  import('./screens/DiffViewerScreen').then((module) => ({
    default: module.DiffViewerScreen,
  }))
)
const PortalViewerScreen = lazy(() =>
  import('./screens/PortalViewerScreen').then((module) => ({
    default: module.PortalViewerScreen,
  }))
)
const NotFoundScreen = lazy(() =>
  import('./screens/NotFoundScreen').then((module) => ({
    default: module.NotFoundScreen,
  }))
)
const BackupsScreen = lazy(() =>
  import('./screens/BackupsScreen').then((module) => ({
    default: module.BackupsScreen,
  }))
)
const DataBackupsScreen = lazy(() =>
  import('./screens/DataBackupsScreen').then((module) => ({
    default: module.DataBackupsScreen,
  }))
)
const ProfileScreen = lazy(() =>
  import('./screens/ProfileScreen').then((module) => ({
    default: module.ProfileScreen,
  }))
)
const WorkspaceSettingsScreen = lazy(() =>
  import('./screens/WorkspaceSettingsScreen').then((module) => ({
    default: module.WorkspaceSettingsScreen,
  }))
)
const InstanceScreen = lazy(() =>
  import('./screens/InstanceScreen').then((module) => ({
    default: module.InstanceScreen,
  }))
)
const AuditLogsScreen = lazy(() =>
  import('./screens/AuditLogsScreen').then((module) => ({
    default: module.AuditLogsScreen,
  }))
)
const EmailConfirmedScreen = lazy(() =>
  import('./screens/EmailConfirmedScreen').then((module) => ({
    default: module.EmailConfirmedScreen,
  }))
)
const OAuthCallbackScreen = lazy(() =>
  import('./screens/OAuthCallbackScreen').then((module) => ({
    default: module.OAuthCallbackScreen,
  }))
)

// Loading fallback component
function LoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Spinner size="lg" />
    </div>
  )
}

function App() {
  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ProfileProvider>
          <ToastProvider>
            <WorkspaceInitializer>
              <UrnOverrideProvider>
                <BrowserRouter>
                  <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                      {/* Public Routes */}
                      <Route path="/auth" element={<AuthScreen />} />
                      <Route path="/email-confirmed" element={<EmailConfirmedScreen />} />

                      {/* Protected Routes */}
                      <Route
                        path="/"
                        element={
                          <ProtectedRoute>
                            <DashboardScreen />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/connections"
                        element={
                          <ProtectedRoute>
                            <ConnectionsScreen />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/oauth/callback"
                        element={
                          <ProtectedRoute>
                            <OAuthCallbackScreen />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/connections/:connectionId/viewer"
                        element={
                          <ProtectedRoute>
                            <PortalViewerScreen />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/comparisons"                      element={
                          <ProtectedRoute>
                            <ComparisonsScreen />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/compare"
                        element={
                          <ProtectedRoute>
                            <CompareSetupScreen />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/diff/:comparisonId"
                        element={
                          <ProtectedRoute>
                            <DiffViewerScreen />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/backups"
                        element={
                          <ProtectedRoute>
                            <BackupsScreen />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/data-backups"
                        element={
                          <ProtectedRoute>
                            <DataBackupsScreen />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/profile"
                        element={
                          <ProtectedRoute>
                            <ProfileScreen />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/workspace/settings"
                        element={
                          <ProtectedRoute>
                            <WorkspaceSettingsScreen />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/workspace/audit-logs"
                        element={
                          <ProtectedRoute>
                            <AuditLogsScreen />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/instance/:id"
                        element={
                          <ProtectedRoute>
                            <InstanceScreen />
                          </ProtectedRoute>
                        }
                      />

                      {/* 404 Not Found */}
                      <Route path="/404" element={<NotFoundScreen />} />
                      <Route path="*" element={<Navigate to="/404" replace />} />
                    </Routes>
                  </Suspense>
                </BrowserRouter>
              </UrnOverrideProvider>
            </WorkspaceInitializer>
          </ToastProvider>
        </ProfileProvider>
      </AuthProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
